/** ORGANIZAREA DE ȘANTIER ca strat al machetei unificate: gardul pe lot,
 *  porțile, spălarea roților, drumul provizoriu, barăcile și depozitele
 *  fiecărei faze, macaralele cu fereastra deschisă — toate din documentul
 *  planificării (`cost_model/santier`, `santier.ts`), nimic inventat.
 *
 *  Coordonatele documentului sunt ale planului de situație, deci trec prin
 *  `punctInCadru` ca restul machetei și stau direct în `lume`. Grupul fix
 *  (gard, porți, spălare, drum) stă cât ține organizarea provizorie; barăcile
 *  și depozitele fiecărei faze stau în grupul ei; macaralele se refac pe
 *  săptămână, cu brațul deasupra ultimului nivel turnat al unității lor. */
import * as THREE from "three";
import * as BGU from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { punctInCadru } from "../canonical";
import type { CanonicalDoc } from "../canonical";
import { camioaneleLa, fazaLa, macaraleleLa, organizareaProvizorie, type OrganizareSantier } from "./santier";
import type { StratAtasat } from "../macheta/tipuri";

type Pt = [number, number];

/** Texturi procedurale, ca în macheta de situație (`texturi3d`): tabla
 *  nervurată a containerului cu ușa și fereastra lui, plasa gardului. */
function texturaContainer(): THREE.Texture {
  const c = document.createElement("canvas"); c.width = 256; c.height = 128;
  const g = c.getContext("2d")!;
  g.fillStyle = "#e9e9e4"; g.fillRect(0, 0, 256, 128);
  for (let x = 0; x < 256; x += 8) { g.fillStyle = x % 16 ? "#dcdcd6" : "#f2f2ee"; g.fillRect(x, 0, 8, 128); }
  g.fillStyle = "#5f7f98"; g.fillRect(0, 0, 256, 6); g.fillRect(0, 122, 256, 6);   // profilele de sus și de jos
  g.fillStyle = "#7f8f98"; g.fillRect(20, 30, 36, 90);                              // ușa
  g.fillStyle = "#9fc3d6"; g.fillRect(96, 34, 44, 40); g.fillRect(170, 34, 44, 40);  // ferestrele
  g.fillStyle = "#4a5a66"; g.fillRect(96, 54, 44, 2); g.fillRect(170, 54, 44, 2);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}
function texturaGard(): THREE.Texture {
  const c = document.createElement("canvas"); c.width = 64; c.height = 64;
  const g = c.getContext("2d")!;
  g.clearRect(0, 0, 64, 64);
  g.strokeStyle = "#8a9299"; g.lineWidth = 2;
  for (let i = -64; i < 128; i += 8) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + 64, 64); g.stroke(); g.beginPath(); g.moveTo(i + 64, 0); g.lineTo(i, 64); g.stroke(); }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

export interface StratSantier extends StratAtasat {
  /** aduce șantierul la săptămâna de luni `iso`; `hCorp(unitate)` = cota ultimului nivel turnat al unității */
  aplica: (iso: string, aprins: boolean, hCorp: (unitate: string) => number) => void;
}

export function construiesteSantier(sit: CanonicalDoc, origine: [number, number], zTeren: number, o: OrganizareSantier): StratSantier {
  const pt = punctInCadru(sit.crs, origine);
  const P = (p: number[]): Pt => pt(p) as Pt;
  const poly = (pts: number[][]) => pts.map(P);
  const grup = new THREE.Group(); grup.name = "santier"; grup.visible = false;
  const deArs: { dispose(): void }[] = [];
  const texCont = texturaContainer(), texGard = texturaGard();
  const M = {
    baraca: new THREE.MeshStandardMaterial({ map: texCont, roughness: 0.7 }),
    baracaAcoperis: new THREE.MeshStandardMaterial({ color: 0x5f7f98, roughness: 0.6 }),
    gard: new THREE.MeshStandardMaterial({ map: texGard, alphaMap: texGard, transparent: true, alphaTest: 0.3, side: THREE.DoubleSide, roughness: 0.7, color: 0xb8c0c8 }),
    stalpGard: new THREE.MeshStandardMaterial({ color: 0x6a7278, roughness: 0.6, metalness: 0.4 }),
    poartaCamioane: new THREE.MeshStandardMaterial({ color: 0xd94f2b, roughness: 0.6 }),
    poartaPietoni: new THREE.MeshStandardMaterial({ color: 0x2b7bd9, roughness: 0.6 }),
    drum: new THREE.MeshStandardMaterial({ color: 0x7d8489, roughness: 0.9 }),
    spalare: new THREE.MeshStandardMaterial({ color: 0x9aa6b0, roughness: 0.5 }),
    apa: new THREE.MeshStandardMaterial({ color: 0x5f7f98, roughness: 0.1, metalness: 0.2 }),
    fier: new THREE.MeshStandardMaterial({ color: 0x6d5236, roughness: 0.8 }),
    cofraj: new THREE.MeshStandardMaterial({ color: 0xc9bd9f, roughness: 0.85 }),
    macara: new THREE.MeshStandardMaterial({ color: 0xf0b400, roughness: 0.5 }),
    macaraInchis: new THREE.MeshStandardMaterial({ color: 0x8d6a00, roughness: 0.6 }),
    camion: new THREE.MeshStandardMaterial({ color: 0xe4e6e8, roughness: 0.6, metalness: 0.2 }),
    bena: new THREE.MeshStandardMaterial({ color: 0x6f7378, roughness: 0.8 }),
    roata: new THREE.MeshStandardMaterial({ color: 0x24262a, roughness: 0.9 }),
  };
  deArs.push(texCont, texGard, ...Object.values(M));
  const fix = new THREE.Group();
  const faze = new Map<string, THREE.Group>();
  const macarale = new THREE.Group();
  const camioane = new THREE.Group();
  grup.add(fix, macarale, camioane);

  const segment = (a: Pt, b: Pt, z0: number, z1: number, lat: number): THREE.BufferGeometry => {
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const g = new THREE.BoxGeometry(Math.hypot(dx, dy), lat, z1 - z0);
    g.rotateZ(Math.atan2(dy, dx)); g.translate((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (z0 + z1) / 2);
    return g;
  };
  const prisma = (pts: Pt[], z0: number, z1: number): THREE.BufferGeometry => {
    const sh = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y)));
    const g = new THREE.ExtrudeGeometry(sh, { depth: z1 - z0, bevelEnabled: false }); g.translate(0, 0, z0);
    return g;
  };
  const uneste = (gs: THREE.BufferGeometry[]): THREE.BufferGeometry | null => {
    const ok = gs.filter((g) => g.attributes.position?.count > 0).map((g) => (g.index ? g.toNonIndexed() : g));
    if (!ok.length) return null;
    for (const g of ok) { for (const k of Object.keys(g.attributes)) if (k !== "position" && k !== "normal" && k !== "uv") g.deleteAttribute(k); if (!g.attributes.normal) g.computeVertexNormals(); }
    return BGU.mergeGeometries(ok, false);
  };
  const adauga = (parinte: THREE.Object3D, g: THREE.BufferGeometry | null, m: THREE.Material, umbra = true) => {
    if (!g) return;
    const mesh = new THREE.Mesh(g, m); mesh.castShadow = umbra; mesh.receiveShadow = true;
    deArs.push(g); parinte.add(mesh);
  };

  // --- gardul: plasa pe panouri cu stâlpi, porțile, spălarea, drumul, paza ---
  const ax = poly(o.gard.axis);
  const hGard = o.gard.inaltime_m || 2;
  const panouri: THREE.BufferGeometry[] = [], stalpi: THREE.BufferGeometry[] = [];
  for (let i = 0; i + 1 < ax.length; i++) {
    const a = ax[i], b = ax[i + 1]; const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const panou = new THREE.PlaneGeometry(L, hGard);
    // uv-ul plasei: un ochi la 10 cm
    const uv = panou.attributes.uv as THREE.BufferAttribute;
    for (let k = 0; k < uv.count; k++) uv.setXY(k, uv.getX(k) * L / 0.5, uv.getY(k) * hGard / 0.5);
    panou.rotateX(Math.PI / 2); panou.rotateZ(Math.atan2(b[1] - a[1], b[0] - a[0]));
    panou.translate((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, zTeren + hGard / 2);
    panouri.push(panou);
    const n = Math.max(1, Math.round(L / 3.5));
    for (let k = 0; k <= n; k++) {
      const t = k / n; const s = new THREE.CylinderGeometry(0.03, 0.03, hGard, 6);
      s.rotateX(Math.PI / 2); s.translate(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, zTeren + hGard / 2); stalpi.push(s);
    }
  }
  adauga(fix, uneste(panouri), M.gard, false);
  adauga(fix, uneste(stalpi), M.stalpGard, false);
  for (const r of o.drumuri) {
    const a = poly(r.axis); const g: THREE.BufferGeometry[] = [];
    for (let i = 0; i + 1 < a.length; i++) g.push(segment(a[i], a[i + 1], zTeren + 0.02, zTeren + 0.05, r.latime_m));
    adauga(fix, uneste(g), M.drum, false);
  }
  for (const p of o.porti) {
    const a = poly(p.axis);
    adauga(fix, segment(a[0], a[1], zTeren, zTeren + 2.2, 0.2), p.fel === "vehicle" ? M.poartaCamioane : M.poartaPietoni);
  }
  const spal = poly(o.spalare_roti.poly);
  adauga(fix, prisma(spal, zTeren, zTeren + 0.25), M.spalare, false);
  adauga(fix, prisma(spal, zTeren + 0.25, zTeren + 0.27), M.apa, false);
  adauga(fix, prisma(poly(o.paza.poly), zTeren, zTeren + 2.6), M.baraca);
  // --- fazele: barăcile (containere etajate) și depozitele -----------------
  for (const f of o.faze) {
    const g = new THREE.Group(); g.visible = false;
    const cab: THREE.BufferGeometry[] = [], acop: THREE.BufferGeometry[] = [];
    for (const c of f.baraci) {
      const z0 = zTeren + (c.etaj ?? 0) * 2.8; const pts = poly(c.poly);
      const box = prisma(pts, z0, z0 + 2.6);
      cab.push(box); acop.push(prisma(pts, z0 + 2.6, z0 + 2.75));
    }
    adauga(g, uneste(cab), M.baraca); adauga(g, uneste(acop), M.baracaAcoperis);
    const dep: Record<string, THREE.BufferGeometry[]> = {};
    for (const x of f.depozite) {
      if (!x.poly) continue;
      const h = x.fel === "concrete_pump" ? 0.3 : x.fel === "waste" ? 1.0 : 1.4;
      (dep[x.fel] ??= []).push(prisma(poly(x.poly), zTeren, zTeren + h));
    }
    for (const [fel, gs] of Object.entries(dep)) {
      adauga(g, uneste(gs), fel === "rebar" ? M.fier : fel === "formwork" ? M.cofraj : fel === "concrete_pump" ? M.spalare : M.drum, fel !== "concrete_pump");
    }
    faze.set(f.id, g); grup.add(g);
  }

  /** MACARAUA TURN zăbrelită: catargul din tronsoane cu diagonale, cabina,
   *  brațul cu contrabrațul, vârful cu tiranții. */
  const macara = (x: number, y: number, zBaza: number, hCarlig: number, raza: number, spre: Pt) => {
    const g = new THREE.Group();
    const h = hCarlig - zBaza;
    const bare: THREE.BufferGeometry[] = [];
    const lat = 1.6;
    // montanții și diagonalele catargului, pe tronsoane de 3 m
    for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      const m = new THREE.BoxGeometry(0.12, 0.12, h); m.translate(x + sx * lat / 2, y + sy * lat / 2, zBaza + h / 2); bare.push(m);
    }
    for (let z = 0; z + 3 <= h + 0.01; z += 3) {
      for (const [a, b] of [[[-1, -1], [1, -1]], [[1, -1], [1, 1]], [[1, 1], [-1, 1]], [[-1, 1], [-1, -1]]] as const) {
        const ax = x + a[0] * lat / 2, ay = y + a[1] * lat / 2, bx = x + b[0] * lat / 2, by = y + b[1] * lat / 2;
        const L = Math.hypot(bx - ax, by - ay, 3);
        const d = new THREE.BoxGeometry(0.06, 0.06, L);
        d.lookAt(new THREE.Vector3(bx - ax, by - ay, 3)); d.translate((ax + bx) / 2, (ay + by) / 2, zBaza + z + 1.5); bare.push(d);
        const o2 = new THREE.BoxGeometry(0.06, 0.06, Math.hypot(bx - ax, by - ay));
        o2.lookAt(new THREE.Vector3(bx - ax, by - ay, 0)); o2.translate((ax + bx) / 2, (ay + by) / 2, zBaza + z); bare.push(o2);
      }
    }
    adauga(g, uneste(bare), M.macara);
    const cab = new THREE.BoxGeometry(2.2, 2.2, 2.4); cab.translate(x + spre[0] * 1.6, y + spre[1] * 1.6, hCarlig + 1.2); adauga(g, cab, M.macaraInchis);
    const ang = Math.atan2(spre[1], spre[0]);
    const brat: THREE.BufferGeometry[] = [];
    for (const dz of [0, 1.1]) for (const dy of [-0.5, 0.5]) {
      const b = new THREE.BoxGeometry(raza, 0.1, 0.1); b.translate(raza / 2 - 7, dy, hCarlig + 2.6 + dz); b.rotateZ(ang); b.translate(x, y, 0); brat.push(b);
    }
    for (let s = 0; s < raza; s += 2.5) {
      const d = new THREE.BoxGeometry(0.07, 1.0, 1.1); d.translate(s - 7, 0, hCarlig + 3.15); d.rotateZ(ang); d.translate(x, y, 0); brat.push(d);
    }
    adauga(g, uneste(brat), M.macara);
    const contra = new THREE.BoxGeometry(14, 1.2, 1.0); contra.translate(-7 - 7, 0, hCarlig + 2.6); contra.rotateZ(ang); contra.translate(x, y, 0); adauga(g, contra, M.macaraInchis);
    const lest = new THREE.BoxGeometry(3, 1.6, 1.4); lest.translate(-7 - 12, 0, hCarlig + 2.2); lest.rotateZ(ang); lest.translate(x, y, 0); adauga(g, lest, M.macaraInchis);
    const varf = new THREE.BoxGeometry(0.5, 0.5, 7); varf.translate(x, y, hCarlig + 6); adauga(g, varf, M.macara);
    const tir: THREE.BufferGeometry[] = [];
    for (const [L, dz] of [[raza - 7, 3.7], [-14, 3.7]] as const) {
      const t = new THREE.BoxGeometry(0.04, 0.04, Math.hypot(L, 6)); t.lookAt(new THREE.Vector3(L, 0, -6)); t.translate(L / 2, 0, hCarlig + 9.5 - 3 + dz - 3.7); t.rotateZ(ang); t.translate(x, y, 0); tir.push(t);
    }
    adauga(g, uneste(tir), M.stalpGard, false);
    macarale.add(g);
  };
  const centrul = (pts: number[][]) => [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length];

  /** CAMIOANELE săptămânii pe drumul de acces: câte intră deodată pe șantier
   *  (cursele pe zi împărțite la un ciclu de ~o oră pe cursă), înșirate pe axa
   *  drumului provizoriu, de la poartă spre interior. Basculanta de pământ cu
   *  bena ei; automalaxorul cu cuva lui. */
  const camion = (x: number, y: number, ang: number, beton: boolean) => {
    const g = new THREE.Group();
    const cab = new THREE.BoxGeometry(2.2, 2.5, 2.6); cab.translate(2.9, 0, 1.3 + 0.5);
    const sasiu = new THREE.BoxGeometry(8.0, 2.4, 0.5); sasiu.translate(0, 0, 1.0);
    const piese: THREE.BufferGeometry[] = [cab, sasiu];
    const roti: THREE.BufferGeometry[] = [];
    for (const rx of [-2.6, -1.3, 2.6]) for (const ry of [-1.1, 1.1]) {
      const r = new THREE.CylinderGeometry(0.55, 0.55, 0.4, 10); r.translate(rx, ry, 0.55); roti.push(r);
    }
    const corp = uneste(piese); const rt = uneste(roti);
    const pune = (geo: THREE.BufferGeometry | null, m: THREE.Material) => {
      if (!geo) return; geo.rotateZ(ang); geo.translate(x, y, zTeren); adauga(g, geo, m);
    };
    pune(corp, M.camion); pune(rt, M.roata);
    if (beton) {
      const cuva = new THREE.CylinderGeometry(1.1, 0.8, 4.6, 12); cuva.rotateZ(Math.PI / 2); cuva.rotateY(0.15); cuva.translate(-1.2, 0, 2.6);
      pune(cuva, M.bena);
    } else {
      const bena = new THREE.BoxGeometry(5.2, 2.4, 1.4); bena.translate(-1.4, 0, 1.95);
      pune(bena, M.bena);
    }
    camioane.add(g);
  };

  const aplica = (iso: string, aprins: boolean, hCorp: (unitate: string) => number) => {
    grup.visible = aprins;
    fix.visible = organizareaProvizorie(o, iso);
    const f = fazaLa(o, iso);
    for (const [id, g] of faze) g.visible = !!f && f.id === id;
    macarale.clear();
    camioane.clear();
    if (!aprins) return;
    // camioanele: pe drumul de acces (primul drum al documentului), câte
    // ~un camion la 12 curse pe zi (un ciclu de o oră pe cursă, 10 ore)
    const sapt = camioaneleLa(o, iso);
    const drum = o.drumuri.find((d) => d.fel === "temporary") ?? o.drumuri[0];
    if (sapt && sapt.pe_zi > 0 && drum && drum.axis.length >= 2) {
      const ax = poly(drum.axis);
      const n = Math.min(8, Math.max(1, Math.round(sapt.pe_zi / 12)));
      const nBeton = Math.round(n * (sapt.camioane_beton / Math.max(sapt.camioane, 1)));
      let lung = 0; const seg: number[] = [];
      for (let i = 0; i + 1 < ax.length; i++) { const L = Math.hypot(ax[i + 1][0] - ax[i][0], ax[i + 1][1] - ax[i][1]); seg.push(L); lung += L; }
      for (let k = 0; k < n; k++) {
        let d = Math.min(lung - 6, 6 + k * 11); if (d < 0) break;
        let i = 0; while (i < seg.length - 1 && d > seg[i]) { d -= seg[i]; i++; }
        const a = ax[i], b = ax[i + 1]; const L = seg[i] || 1; const t = d / L;
        const px = a[0] + (b[0] - a[0]) * t, py = a[1] + (b[1] - a[1]) * t;
        camion(px, py, Math.atan2(b[1] - a[1], b[0] - a[0]), k < nBeton);
      }
    }
    for (const m of macaraleleLa(o, iso)) {
      const [x, y] = P([m.x, m.y]);
      const fz = o.faze.find((ff) => ff.id === f?.id);
      const unit = fz?.baraci.length ? null : null; void unit;
      // brațul spre centrul unității lui: din poligoanele fazei n-avem unitatea, deci spre centrul lotului
      const c = centrul(o.gard.axis.map((p) => P(p)));
      const spre: Pt = [c[0] - x, c[1] - y]; const L = Math.hypot(spre[0], spre[1]) || 1;
      const zBaza = zTeren + (m.in_groapa ? m.cota_baza_m : 0);
      const h = hCorp(m.unitate);
      const hCarlig = m.in_groapa ? zTeren + m.inaltime_carlig_m
        : Math.min(zTeren + m.inaltime_carlig_m, Math.max(zTeren + 26, h + 9) + m.rang_brat * 3);
      macara(x, y, zBaza, hCarlig, m.raza_m, [spre[0] / L, spre[1] / L]);
    }
  };
  return {
    id: "structura" as const, grup, tinta: () => null, evidentiaza: () => undefined, aplica,
    dispose: () => { for (const d of deArs) d.dispose(); grup.clear(); },
  } as StratSantier;
}
